import dynamic from 'next/dynamic'
import { SanityTemplate } from '../services/sanity/types'
import { Product } from '../types/product'

// Type-only imports for props (these are erased at build time)
import type { TmCheckoutProps } from '../templates/tm-checkout'
import type { TmVisitorsGuideProps } from '../templates/tm-visitors-guide'
import type { TmBuildingDetailProps } from '../templates/tm-building-detail'
import type { TmContactProps } from '../templates/tm-contact'
import type { TmContactV2Props } from '../templates/tm-contact-v2'
import type { TmGuestListProps } from '../templates/tm-guest-list'
import type { TmPdpProps } from '../templates/tm-pdp'
import type { TmPlpProps } from '../templates/tm-plp'
import type { TmFlexibleProps } from '../templates/tm-flexible'

// Dynamic imports — each template is code-split into its own chunk.
// SSR is enabled (default) so the server renders full HTML; the client hydrates without layout shifts.
// Loading returns null to avoid CLS if the chunk hasn't arrived during hydration.
const TmVisitorsGuide = dynamic(() => import('../templates/tm-visitors-guide').then((mod) => mod.TmVisitorsGuide), {
  loading: () => null,
})

const TmBuildingDetail = dynamic(() => import('../templates/tm-building-detail').then((mod) => mod.TmBuildingDetail), {
  loading: () => null,
})

const TmContact = dynamic(() => import('../templates/tm-contact').then((mod) => mod.TmContact), { loading: () => null })

const TmContactV2 = dynamic(() => import('../templates/tm-contact-v2').then((mod) => mod.TmContactV2), {
  loading: () => null,
})

const TmCheckout = dynamic(() => import('../templates/tm-checkout').then((mod) => mod.TmCheckout), {
  loading: () => null,
})

const TmPdp = dynamic(() => import('../templates/tm-pdp').then((mod) => mod.TmPdp), { loading: () => null })

const TmGuestList = dynamic(() => import('../templates/tm-guest-list').then((mod) => mod.TmGuestList), {
  loading: () => null,
})

const TmPlp = dynamic(() => import('../templates/tm-plp').then((mod) => mod.TmPlp), { loading: () => null })

const TmFlexible = dynamic(() => import('../templates/tm-flexible').then((mod) => mod.TmFlexible), {
  loading: () => null,
})

export type TemplateSelectorProps = {
  type: SanityTemplate
  template: unknown
  product: Product | null
}

export const TemplateSelector = (props: TemplateSelectorProps) => {
  switch (props.type) {
    case 'tmVisitorGuide':
      return <TmVisitorsGuide {...(props.template as TmVisitorsGuideProps)} />
    case 'tmBuildingDetail':
      return <TmBuildingDetail {...(props.template as TmBuildingDetailProps)} />
    case 'tmContact':
      return <TmContact {...(props.template as TmContactProps)} />
    case 'tmContactV2':
      return <TmContactV2 {...(props.template as TmContactV2Props)} />
    case 'tmCheckout':
      return <TmCheckout {...(props.template as TmCheckoutProps)} product={props.product} />
    case 'tmPdp':
      return <TmPdp {...(props.template as TmPdpProps)} />
    case 'tmGuestList':
      return <TmGuestList {...(props.template as TmGuestListProps)} product={props.product} />
    case 'tmProductListing':
      return <TmPlp {...(props.template as TmPlpProps)} />
    case 'tmFlexible':
      return <TmFlexible {...(props.template as TmFlexibleProps)} />
    default:
      return <div className="flex justify-center my-16">TODO: Template integration</div>
  }
}
